
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";

const SubscriberRegistration = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    address: "",
    emergencyContactName: "",
    emergencyContactEmail: "",
    emergencyContactNumber: "",
    nearestPoliceStation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastAttemptTime, setLastAttemptTime] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateUniquePSID = async () => {
    console.log("Starting PSID generation");
    for (let i = 3; i <= 999; i++) {
      const paddedNumber = i.toString().padStart(3, '0');
      const psid = `PS${paddedNumber}`;
      console.log("Checking PSID:", psid);
      
      try {
        const { data: existingMapping, error: mappingError } = await supabase
          .from('user_psid_mappings')
          .select('psid')
          .eq('psid', psid);

        if (mappingError) {
          console.error('Error checking PSID:', mappingError);
          continue;
        }

        if (!existingMapping || existingMapping.length === 0) {
          console.log("Found available PSID:", psid);
          return psid;
        }
      } catch (error) {
        console.error('Error in PSID check:', error);
        continue;
      }
    }
    throw new Error('No available PSIDs');
  };

  const createPSIDMapping = async (email: string, psid: string, subscriber_id: string) => {
    console.log('Creating PSID mapping for:', email, psid, 'with subscriber_id:', subscriber_id);
    const { error: mappingError } = await supabase
      .from('user_psid_mappings')
      .insert({
        user_email: email,
        psid: psid,
        subscriber_id: subscriber_id
      });

    if (mappingError) {
      console.error('Mapping Error:', mappingError);
      throw mappingError;
    }
    console.log('Created PSID mapping successfully');
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || 
        !formData.phone || !formData.emergencyContactName || !formData.emergencyContactEmail || 
        !formData.emergencyContactNumber || !formData.nearestPoliceStation) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match. Please try again.",
        variant: "destructive",
      });
      return false;
    }

    if (formData.password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastAttempt = now - lastAttemptTime;
    const RATE_LIMIT_DELAY = 15000;
    
    if (timeSinceLastAttempt < RATE_LIMIT_DELAY) {
      const waitSeconds = Math.ceil((RATE_LIMIT_DELAY - timeSinceLastAttempt) / 1000);
      toast({
        title: "Please wait",
        description: `Please wait ${waitSeconds} seconds before trying again.`,
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    setLastAttemptTime(now);

    try {
      console.log("Starting registration process with data:", formData);
      
      // Generate a unique PSID
      const psid = await generateUniquePSID();
      console.log("Generated PSID:", psid);

      // Create subscriber first using edge function
      const { data, error } = await supabase.functions.invoke('create-subscriber', {
        body: {
          subscriberData: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            emergencyContactName: formData.emergencyContactName,
            emergencyContactEmail: formData.emergencyContactEmail,
            emergencyContactPhone: formData.emergencyContactNumber,
            nearestPoliceStation: formData.nearestPoliceStation, // This will be mapped to police_station_name
            psid: psid,
            password: formData.password
          }
        }
      });

      // Enhanced Error Handling from Edge Function
      if (error) {
        console.error('Error response from create-subscriber:', error);
        let message = "Failed to complete registration.";
        let code = null;
         // Try to parse Supabase Function error context
        if (error.context && typeof error.context.json === 'function') {
           try {
             const errorJson = await error.context.json();
             message = errorJson.error || errorJson.message || message;
             code = errorJson.code; // Get specific code if provided
           } catch (_) { /* Ignore parsing error */ }
        } else if (error.message) {
             message = error.message; // Use direct message if context parsing fails
        }
        // Check specific codes/messages passed back
        if (code === "AUTH_USER_EXISTS" || message.includes("User already exists")) {
             toast({ title: "Email already registered", description: "Please log in instead.", variant: "destructive" });
        } else {
             toast({ title: "Registration Error", description: message, variant: "destructive" });
        }
        setIsLoading(false); // Ensure loading stops on error
        return; // Stop execution
      }

      console.log('create-subscriber function reported success:', data);
      const returned_subscriber_id = data?.subscriber_id;

      if (!returned_subscriber_id) {
        console.error('No subscriber_id received in success response:', data);
        throw new Error('Registration partially failed. Please contact support.'); // Throw if success reported but no ID
      }

      // Sign in the user automatically
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email, password: formData.password,
      });

      if (signInError) {
        console.error('Sign in Error after registration:', signInError);
        toast({ title: "Registration Complete", description: "Account created, but auto-login failed. Please log in manually.", variant: "default" });
        navigate('/login');
        return;
      }
      console.log('User signed in successfully');

      // Now create PSID mapping after successful sign in
      await createPSIDMapping(formData.email, psid, data.subscriber_id);

      // Store subscriber_id
      localStorage.setItem('subscriber_id', returned_subscriber_id);
      console.log('Stored subscriber_id in localStorage:', returned_subscriber_id);

      // Get the JWT token from localStorage
      const { data: sessionData } = await supabase.auth.getSession();
      const jwtToken = sessionData.session?.access_token;
      
      if (jwtToken) {
        localStorage.setItem('oneinbox_jwt', jwtToken);
      } else {
        console.warn('JWT token not found in session');
      }

      toast({ title: "Success", description: "Registration completed successfully!" });
      navigate("/plans");
      
    } catch (error: any) {
      console.error("Registration handleSubmit Error:", error);
      // Avoid redundant specific toasts if already handled in Edge Function error parsing
       if (!error.message?.includes("already exists")) { // Don't double-toast for known handled error
           toast({ title: "Error", description: error.message || "An unexpected error occurred.", variant: "destructive" });
       }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Signup for Senior Roll Call</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="john@gmail.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+12512500508"
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Address
              </label>
              <Input
                id="address"
                name="address"
                type="text"
                required
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, City, State, ZIP"
              />
            </div>

            <div>
              <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700">
                Emergency Contact Name
              </label>
              <Input
                id="emergencyContactName"
                name="emergencyContactName"
                type="text"
                required
                value={formData.emergencyContactName}
                onChange={handleChange}
                placeholder="Mike Tyson"
              />
            </div>

            <div>
              <label htmlFor="emergencyContactEmail" className="block text-sm font-medium text-gray-700">
                Emergency Contact Email
              </label>
              <Input
                id="emergencyContactEmail"
                name="emergencyContactEmail"
                type="email"
                required
                value={formData.emergencyContactEmail}
                onChange={handleChange}
                placeholder="mike@example.com"
              />
            </div>

            <div>
              <label htmlFor="emergencyContactNumber" className="block text-sm font-medium text-gray-700">
                Emergency Contact Number
              </label>
              <Input
                id="emergencyContactNumber"
                name="emergencyContactNumber"
                type="tel"
                required
                value={formData.emergencyContactNumber}
                onChange={handleChange}
                placeholder="+17865438989"
              />
            </div>

            <div>
              <label htmlFor="nearestPoliceStation" className="block text-sm font-medium text-gray-700">
                Nearest Police Station
              </label>
              <Input
                id="nearestPoliceStation"
                name="nearestPoliceStation"
                type="text"
                required
                value={formData.nearestPoliceStation}
                onChange={handleChange}
                placeholder="Delaware State Police"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-black hover:bg-gray-800"
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Sign Up"}
          </Button>

          <div className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-800">
              Log In
            </Link>
          </div>

          <div className="flex justify-center space-x-4 text-sm text-gray-600">
            <a href="#" className="hover:text-gray-900">Terms of Service</a>
            <a href="#" className="hover:text-gray-900">Privacy Policy</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriberRegistration;
